import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Mail, Phone, MapPin, GraduationCap, Landmark, Loader2, Search } from 'lucide-react';
import { EditableCardActions } from './EditableCardActions';
import { useEditableCard } from '@/hooks/useEditableCard';
import { updateSchool, getAcademies, searchEtablissement } from '@/api/schools';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import type { AddressSuggestion } from '@/api/schools';
import { getCountryOptions, countryCodeToFlag, getCountryByCode } from '@/constants/countries';
import type { SchoolEditData, SchoolOptions, UpdateSchoolData, Academie } from '@/types/school';
import type { EtablissementSearchResult } from '@/api/schools';

const { priority: PRIORITY_COUNTRIES, others: OTHER_COUNTRIES } = getCountryOptions();

interface GeneralInfoCardProps {
  school: SchoolEditData;
  options: SchoolOptions;
  onSaved: () => void;
}

interface GeneralInfoFormData {
  [key: string]: unknown;
  name: string;
  email: string | null;
  phone: string | null;
  emailSender: string | null;
  domainSender: string | null;
  networkId: number | null;
  address: string | null;
  addressComplement: string | null;
  addressHeadoffice: string | null;
  addressHeadofficeComplement: string | null;
  isEducationNationale: boolean;
  rne: string | null;
  codeAcademie: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

export function GeneralInfoCard({ school, options, onSaved }: GeneralInfoCardProps) {
  const [academies, setAcademies] = useState<Academie[]>([]);
  const [loadingAcademies, setLoadingAcademies] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<EtablissementSearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const initialData: GeneralInfoFormData = {
    name: school.name,
    email: school.email,
    phone: school.phone,
    emailSender: school.emailSender ?? null,
    domainSender: school.domainSender,
    networkId: school.networkId,
    address: school.address,
    addressComplement: school.addressComplement ?? null,
    addressHeadoffice: school.addressHeadoffice,
    addressHeadofficeComplement: school.addressHeadofficeComplement ?? null,
    isEducationNationale: school.isEducationNationale || false,
    rne: school.rne ?? null,
    codeAcademie: school.codeAcademie ?? null,
    country: school.country || 'FR',
    latitude: school.latitude ?? null,
    longitude: school.longitude ?? null,
  };

  const { isEditing, formData, isSaving, error, startEditing, cancelEditing, updateField, save } =
    useEditableCard<GeneralInfoFormData>({
      initialData,
      onSave: async (data) => {
        const payload: UpdateSchoolData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          emailSender: data.emailSender,
          domainSender: data.domainSender,
          networkId: data.networkId,
          address: data.address,
          addressComplement: data.addressComplement,
          addressHeadoffice: data.addressHeadoffice,
          addressHeadofficeComplement: data.addressHeadofficeComplement,
          isEducationNationale: data.isEducationNationale,
          rne: data.rne,
          codeAcademie: data.codeAcademie,
          country: data.country,
          latitude: data.latitude,
          longitude: data.longitude,
        };
        await updateSchool(school.code, payload);
      },
      onSaved,
    });

  // Load academies
  useEffect(() => {
    if (academies.length === 0) {
      const loadAcademies = async () => {
        setLoadingAcademies(true);
        try {
          const data = await getAcademies();
          setAcademies(data);
        } catch (err) {
          console.error('Error loading academies:', err);
        } finally {
          setLoadingAcademies(false);
        }
      };
      loadAcademies();
    }
  }, [academies.length]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced autocomplete search
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchEtablissement(searchQuery);
        setSearchResults(response.results);
        setShowResults(true);
      } catch (err) {
        console.error('Error searching:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectResult = (result: EtablissementSearchResult) => {
    if (result.rne) updateField('rne', result.rne);
    if (result.codeAcademie) updateField('codeAcademie', result.codeAcademie);
    if (result.name) updateField('name', result.name);
    if (result.email) updateField('email', result.email);
    if (result.phone) updateField('phone', result.phone);
    if (result.address) {
      const fullAddress = [result.address, result.postalCode, result.city]
        .filter(Boolean)
        .join(' ');
      updateField('address', fullAddress);
    }
    if (result.latitude && result.longitude) {
      updateField('latitude', result.latitude);
      updateField('longitude', result.longitude);
    }
    setShowResults(false);
    setSearchQuery('');
  };

  if (!isEditing) {
    return (
      <Card>
        <CardHeader
          icon={<Building2 />}
          iconColor="blue"
          actions={
            <EditableCardActions
              isEditing={false}
              isSaving={false}
              onEdit={startEditing}
              onSave={save}
              onCancel={cancelEditing}
            />
          }
        >
          <CardTitle>Informations générales</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 items-center">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Code</p>
                <p className="font-mono">{school.code}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Nom</p>
                <p>{school.name}</p>
              </div>
            </div>
            <div className="flex justify-center">
              {school.logoUrl ? (
                <img
                  src={school.logoUrl}
                  alt={`Logo ${school.name}`}
                  className="h-32 w-32 object-contain rounded-lg"
                />
              ) : (
                <div className="h-32 w-32 rounded-lg bg-muted flex items-center justify-center">
                  <Building2 className="h-12 w-12 text-muted-foreground/50" />
                </div>
              )}
            </div>
          </div>

          {(school.email || school.phone) && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
              {school.email && (
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-sm">{school.email}</p>
                  </div>
                </div>
              )}
              {school.phone && (
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Telephone</p>
                    <p className="text-sm">{school.phone}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {(school.address || school.country) && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Adresse</p>
                <p className="text-sm whitespace-pre-line">{school.address}</p>
                {school.addressComplement && (
                  <p className="text-sm text-muted-foreground">{school.addressComplement}</p>
                )}
                {school.country && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {countryCodeToFlag(school.country)}{' '}
                    {getCountryByCode(school.country)?.label || school.country}
                  </p>
                )}
              </div>
            </div>
          )}

          {(school.isEducationNationale || school.rne || school.codeAcademie) && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
              <div className="flex items-start gap-2">
                <GraduationCap className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Education Nationale</p>
                  <p className="text-sm">{school.isEducationNationale ? 'Oui' : 'Non'}</p>
                </div>
              </div>
              {school.rne && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Code RNE</p>
                  <p className="text-sm font-mono">{school.rne}</p>
                </div>
              )}
              {school.codeAcademie && (
                <div className="flex items-start gap-2">
                  <Landmark className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Academie</p>
                    <p className="text-sm">{academies.find((a) => a.code === school.codeAcademie)?.name || school.codeAcademie}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {school.maintenance && (
            <div className="pt-2">
              <Badge variant="destructive">Maintenance</Badge>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Edit mode
  return (
    <Card>
      <CardHeader
        icon={<Building2 />}
        iconColor="blue"
        actions={
          <EditableCardActions
            isEditing={true}
            isSaving={isSaving}
            onEdit={startEditing}
            onSave={save}
            onCancel={cancelEditing}
          />
        }
      >
        <CardTitle>Informations générales</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Education Nationale Toggle */}
        <div>
          <div className="p-4 border rounded-lg bg-muted/30 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <Label className="text-base font-medium">
                    Etablissement Education Nationale
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    École reconnue par l'Éducation Nationale française
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  type="button"
                  className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                    formData.isEducationNationale
                      ? 'bg-white shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => updateField('isEducationNationale', true)}
                >
                  Oui
                </button>
                <button
                  type="button"
                  className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                    !formData.isEducationNationale
                      ? 'bg-white shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => {
                    updateField('isEducationNationale', false);
                    updateField('rne', null);
                    updateField('codeAcademie', null);
                  }}
                >
                  Non
                </button>
              </div>
            </div>
          </div>

          {formData.isEducationNationale && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                Recherche dans l'annuaire Education Nationale
              </h3>

              <div className="space-y-2" ref={searchRef}>
                <Label htmlFor="gen-etab-search">Rechercher un etablissement</Label>
                <div className="relative">
                  <Input
                    id="gen-etab-search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setShowResults(true)}
                    placeholder="Code RNE ou nom de l'etablissement..."
                    className="pr-10"
                  />
                  {searching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {!searching && searchQuery.length >= 3 && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Search className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  {showResults && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                      {searchResults.map((result, index) => (
                        <button
                          key={result.rne || index}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-muted border-b last:border-b-0"
                          onClick={() => handleSelectResult(result)}
                        >
                          <div className="font-medium text-sm">{result.name || 'Sans nom'}</div>
                          <div className="text-xs text-muted-foreground">
                            {result.rne && <span className="font-mono">{result.rne}</span>}
                            {result.city && <span> - {result.city}</span>}
                            {result.postalCode && <span> ({result.postalCode})</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {showResults && searchResults.length === 0 && !searching && searchQuery.length >= 3 && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-3 text-sm text-muted-foreground">
                      Aucun resultat trouve
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Tapez au moins 3 caracteres pour rechercher par code RNE ou nom
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gen-rne">Code RNE (UAI)</Label>
              <Input
                id="gen-rne"
                value={formData.rne || ''}
                onChange={(e) => updateField('rne', e.target.value.toUpperCase() || null)}
                placeholder="Ex: 0750001A"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gen-academie">Academie</Label>
              <Select
                value={formData.codeAcademie || 'none'}
                onValueChange={(value) =>
                  updateField('codeAcademie', value === 'none' ? null : value)
                }
                disabled={loadingAcademies}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingAcademies ? 'Chargement...' : 'Selectionner une academie'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {academies.map((academie) => (
                    <SelectItem key={academie.code} value={academie.code}>
                      {academie.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gen-name">Nom de l'école</Label>
            <Input
              id="gen-name"
              value={formData.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-email">Email de contact</Label>
            <Input
              id="gen-email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => updateField('email', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-phone">Telephone</Label>
            <Input
              id="gen-phone"
              value={formData.phone || ''}
              onChange={(e) => updateField('phone', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-emailSender">Email expediteur</Label>
            <Input
              id="gen-emailSender"
              type="email"
              value={formData.emailSender || ''}
              onChange={(e) => updateField('emailSender', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-domainSender">Domaine expediteur</Label>
            <Input
              id="gen-domainSender"
              value={formData.domainSender || ''}
              onChange={(e) => updateField('domainSender', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-network">Reseau</Label>
            <Select
              value={formData.networkId?.toString() || 'none'}
              onValueChange={(value) =>
                updateField('networkId', value === 'none' ? null : parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {options.networks.map((n) => (
                  <SelectItem key={n.id} value={n.id.toString()}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gen-country">Pays</Label>
            <Select
              value={formData.country || 'FR'}
              onValueChange={(value) => {
                updateField('country', value);
                updateField('latitude', null);
                updateField('longitude', null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un pays" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
                <div className="border-t my-1" />
                {OTHER_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gen-address">Adresse</Label>
            <AddressAutocomplete
              id="gen-address"
              value={formData.address || ''}
              country={formData.country || 'FR'}
              onChange={(address) => updateField('address', address || null)}
              onSelect={(suggestion: AddressSuggestion) => {
                updateField('address', suggestion.label);
                updateField('latitude', suggestion.latitude);
                updateField('longitude', suggestion.longitude);
              }}
              placeholder="Saisir une adresse..."
            />
            <Input
              id="gen-addressComplement"
              value={formData.addressComplement || ''}
              onChange={(e) => updateField('addressComplement', e.target.value || null)}
              placeholder="Complément d'adresse"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-addressHq">Siège social</Label>
            <AddressAutocomplete
              id="gen-addressHq"
              value={formData.addressHeadoffice || ''}
              country={formData.country || 'FR'}
              onChange={(address) => updateField('addressHeadoffice', address || null)}
              onSelect={(suggestion: AddressSuggestion) => {
                updateField('addressHeadoffice', suggestion.label);
              }}
              placeholder="Adresse du siège social"
            />
            <Input
              id="gen-addressHqComplement"
              value={formData.addressHeadofficeComplement || ''}
              onChange={(e) => updateField('addressHeadofficeComplement', e.target.value || null)}
              placeholder="Complément d'adresse"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
